import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { environment } from '../../environments/environment';
import { filter, take } from 'rxjs/operators';

/**
 * Estructura de un KPI temporal devuelto por el backend en el campo `monthly`.
 * Construido por buildDelta() en dashboardService.
 */
interface MonthlyKpi {
  current: number | null;
  previous: number | null;
  delta_pct: number | null;
  delta_label: string;
}

interface MonthlyMetrics {
  period: {
    current_start: string;
    current_end: string;
    previous_start: string;
    previous_end: string;
  };
  estimations: MonthlyKpi;
  logins: MonthlyKpi;
  active_users: MonthlyKpi;
  mmre: MonthlyKpi;
  avg_tokens_per_estimation: MonthlyKpi;
  estimations_per_active_user: MonthlyKpi;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {
  isGlobalMode: boolean = false;
  metrics: any = null;
  loading: boolean = true;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const isSuperAdmin = this.authService.isSuperAdmin();

    if (isSuperAdmin) {
      this.isGlobalMode = !this.authService.getActiveTenantId();
      this.loadMetrics();
    } else {
      this.authService.permissions$.pipe(
        filter(p => p && Object.keys(p).length > 0),
        take(1)
      ).subscribe(() => {
        const currentUser = this.authService.getCurrentUser();

        const isTenantAdmin = !!currentUser?.tenant?.id &&
          (this.authService.hasPermission('menu:users:manage') || this.authService.hasPermission('all:manage'));

        if (!isTenantAdmin) {
          this.router.navigate(['/app/proyectos'], { replaceUrl: true });
        } else {
          this.isGlobalMode = false;
          this.loadMetrics();
        }
      });
    }
  }

  loadMetrics(): void {
    this.http.get<any>(`${environment.api_service_dashboard_metrics}`).subscribe({
      next: (data) => {
        this.metrics = data;
        this.loading = false;
      },
      error: (err) => {
        console.error("Error cargando métricas:", err);
        this.loading = false;
      }
    });
  }

  // ===========================================================================
  // KPIs existentes
  // ===========================================================================

  get tokenPercentage(): number {
    if (!this.metrics || !this.metrics.tokens.total) return 0;
    return Math.min(100, Math.round((this.metrics.tokens.consumed / this.metrics.tokens.total) * 100));
  }

  // ===========================================================================
  // KPIs del mes (provienen de metrics.monthly desde F5.1)
  // ===========================================================================

  /** Acceso seguro al bloque mensual. Devuelve null si no llegó del backend. */
  get monthly(): MonthlyMetrics | null {
    return this.metrics?.monthly ?? null;
  }

  // ---- Fila 2: Actividad del mes -------------------------------------------

  get estimationsCurrent(): number       { return this.monthly?.estimations.current ?? 0; }
  get estimationsDeltaLabel(): string    { return this.monthly?.estimations.delta_label ?? '—'; }
  get estimationsSemantic(): 'good' | 'warning' | 'danger' | 'neutral' {
    return this.deltaSemantic(this.monthly?.estimations.delta_pct, 'higher_better');
  }

  get activeUsersCurrent(): number       { return this.monthly?.active_users.current ?? 0; }
  get activeUsersDeltaLabel(): string    { return this.monthly?.active_users.delta_label ?? '—'; }
  get activeUsersSemantic(): 'good' | 'warning' | 'danger' | 'neutral' {
    return this.deltaSemantic(this.monthly?.active_users.delta_pct, 'higher_better');
  }

  get loginsCurrent(): number            { return this.monthly?.logins.current ?? 0; }
  get loginsDeltaLabel(): string         { return this.monthly?.logins.delta_label ?? '—'; }
  get loginsSemantic(): 'good' | 'warning' | 'danger' | 'neutral' {
    return this.deltaSemantic(this.monthly?.logins.delta_pct, 'higher_better');
  }

  // ---- Fila 3: Calidad y eficiencia del mes --------------------------------

  /** MMRE: menor es mejor. */
  get mmreCurrent(): number | null       { return this.monthly?.mmre.current ?? null; }
  get mmreDeltaLabel(): string           { return this.monthly?.mmre.delta_label ?? '—'; }
  get mmreSemantic(): 'good' | 'warning' | 'danger' | 'neutral' {
    return this.deltaSemantic(this.monthly?.mmre.delta_pct, 'lower_better');
  }

  get avgTokensCurrent(): number | null  { return this.monthly?.avg_tokens_per_estimation.current ?? null; }
  get avgTokensDeltaLabel(): string      { return this.monthly?.avg_tokens_per_estimation.delta_label ?? '—'; }
  get avgTokensSemantic(): 'good' | 'warning' | 'danger' | 'neutral' {
    return this.deltaSemantic(this.monthly?.avg_tokens_per_estimation.delta_pct, 'lower_better');
  }

  get estPerUserCurrent(): number        { return this.monthly?.estimations_per_active_user.current ?? 0; }
  get estPerUserDeltaLabel(): string     { return this.monthly?.estimations_per_active_user.delta_label ?? '—'; }
  get estPerUserSemantic(): 'good' | 'warning' | 'danger' | 'neutral' {
    return this.deltaSemantic(this.monthly?.estimations_per_active_user.delta_pct, 'higher_better');
  }

  // ---- Helpers --------------------------------------------------------------

  /**
   * Calcula el "semantic" del delta para colorear el subtitle.
   * - higher_better: subir es bueno (verde), bajar es malo (rojo).
   * - lower_better:  bajar es bueno (verde), subir es malo (rojo).
   * Umbrales: |delta| < 5% → neutral, < 25% → warning, ≥ 25% → danger/good.
   */
  private deltaSemantic(
    deltaPct: number | null | undefined,
    direction: 'higher_better' | 'lower_better'
  ): 'good' | 'warning' | 'danger' | 'neutral' {
    if (deltaPct === null || deltaPct === undefined) return 'neutral';
    const abs = Math.abs(deltaPct);
    if (abs < 5) return 'neutral';

    const isPositive = deltaPct > 0;
    const isGoodDirection = (direction === 'higher_better' && isPositive)
                         || (direction === 'lower_better'  && !isPositive);

    if (abs >= 25) return isGoodDirection ? 'good' : 'danger';
    return isGoodDirection ? 'good' : 'warning';
  }

  /** Texto secundario uniforme: "vs mes anterior: +12.5%". */
  buildDeltaSubtitle(deltaLabel: string): string {
    return `vs mismo período mes anterior: ${deltaLabel}`;
  }
}
